import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { Subject } from 'rxjs';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';

@Component({
  selector: 'es-details-entity-desktop',
  template: `
    <div class="es-entity-card desktop" [ngClass]="{ 'is-valid': entityIsValid, 'is-invalid': entityHasError }">
      <div class="es-entity-card-header">
        <div class="es-entity-card-header-info">
          <span class="es-entity-is-valid" *ngIf="entityIsValid">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.0757 16.7728L6.17573 12.8728C5.94142 12.6385 5.94142 12.2586 6.17573 12.0243L7.02424 11.1757C7.25854 10.9414 7.63846 10.9414 7.87277 11.1757L10.5 13.8029L16.1272 8.17573C16.3615 7.94142 16.7415 7.94142 16.9758 8.17573L17.8243 9.02426C18.0586 9.25857 18.0586 9.63846 17.8243 9.87279L10.9243 16.7728C10.6899 17.0071 10.31 17.0071 10.0757 16.7728Z"
                fill="currentColor"
              />
            </svg>
          </span>

          <h3>{{ title | translate }}</h3>
        </div>

        <div class="es-entity-card-header-actions d-print-none d-flex align-items-center">
          <button type="button" class="btn btn-link" *ngIf="isShowEdit && !!entity" (click)="edit.emit(entity?.id)">
            <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                fill="currentColor"
              />
            </svg>
            <span class="pl-3">{{ 'BUTTON.EDIT' | translate }}</span>
          </button>

          <button type="button" class="btn btn-link ml-2" *ngIf="isShowDelete" (click)="remove.emit(entity?.id)">
            <i class="fa fa-trash mr-2"></i>
            <span>{{ 'BUTTON.DELETE' | translate }}</span>
          </button>
        </div>
      </div>

      <div class="es-entity-card-body">
        <div class="es-entity-card-body-icon">
          <img [src]="icon" [alt]="title" />
        </div>
        <div class="es-entity-card-body-data">
          <ng-container *ngIf="!entity">
            <p *ngIf="!!subTitle">
              {{ subTitle }}
            </p>

            <span class="text-hint" *ngIf="!!subTitleHint">
              {{ subTitleHint }}
            </span>
          </ng-container>

          <ng-container *ngIf="!!entity">
            <ng-container *ngTemplateOutlet="entityItem; context: { entityItem: entity }"></ng-container>
          </ng-container>
        </div>

        <div class="es-entity-card-body-add-hover" *ngIf="!entity" (click)="edit.emit()">
          <div class="es-entity-card-body-add-hover-icon">
            <svg width="40" height="40" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="13.5" cy="13.5" r="13.5" fill="#03BCBE" />
              <path d="M6 12H21V15H6V12Z" fill="white" />
              <path d="M12 21V6H15V21H12Z" fill="white" />
            </svg>
          </div>
          <span>{{ 'BUTTON.CLICK_TO_ADD' | translate }}</span>
        </div>
      </div>
    </div>

    <ng-template #entityItem let-entityItem="entityItem" let-index="index">
      <div class="es-entity-card-body-data-rows" *ngIf="isShowEdit">
        <div class="row">
          <div class="col-2 key">{{ 'LABELS.NAME' | translate }}</div>

          <ng-container *ngIf="!getValidationText('name'); else missedName">
            <div class="col-10 value">{{ entityItem?.name }}</div>
          </ng-container>
          <ng-template #missedName>
            <div class="col-10 value text-error">{{ getValidationText('name') | translate }}</div>
          </ng-template>
        </div>

        <div class="row">
          <div class="col-2 key">{{ 'LABELS.ADDRESS' | translate }}</div>

          <ng-container *ngIf="isValidESEntityAddress(); else missedAdd">
            <div class="col-10 value" *ngIf="!getValidationText('address')">
              <address-details [address]="entityItem.address"></address-details>
            </div>
            <div class="col-10 value text-error" *ngIf="getValidationText('address')">
              {{ getValidationText('address') | translate }}
            </div>
          </ng-container>
          <ng-template #missedAdd>
            <div class="col-10 value text-error">{{ 'BACK_ERROR.MISSING_ADDRESS_PARTS' | translate }}</div>
          </ng-template>
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: ['./es-details-entity-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsDetailsEntityDesktopComponent implements OnChanges, OnDestroy {
  @Input() public entity: EntityModel;
  @Input() public title: string;
  @Input() public subTitle: string;
  @Input() public subTitleHint: string;
  @Input() public icon: string;
  @Input() public isShowEdit: boolean;
  @Input() public isShowDelete: boolean;

  @Output() public edit: EventEmitter<number> = new EventEmitter<number>();
  @Output() public remove: EventEmitter<number> = new EventEmitter<number>();

  public entityIsValid: boolean;
  public entityHasError: boolean;

  public enumIdentifierQualifier = enumIdentifierQualifier;
  public enumEntityModelCode = enumEntityModelCode;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor() {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.entity) {
      this.getIsValid();
      this.getHasError();
    }
    if (changes?.entities) {
      this.getIsValid();
      this.getHasError();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public getValidationText(fieldName: string): string {
    return EntityValidationHelper.getEntityValidationText(this.entity, fieldName);
  }

  public isValidESEntityAddress(): boolean {
    return EntityValidationHelper.isValidEntityAddress(this.entity?.address);
  }

  private getIsValid(): void {
    this.entityIsValid = !!this.entity && this.entity?.validationMessages?.length === 0 && this.isValidESEntityAddress();
  }

  private getHasError(): void {
    this.entityHasError = !!this.entity && (this.entity?.validationMessages?.length > 0 || !this.isValidESEntityAddress());
  }
}
